import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import type { Chapter, LessonStep } from '@/types/storytelling'
import type { Json } from '@/types/database'

interface ResponseRecord {
  step: number
  choiceMade: string
}

export interface LessonState {
  chapter: Chapter
  currentStepIndex: number
  currentStep: LessonStep
  responses: ResponseRecord[]
  isComplete: boolean
}

const TOTAL_STEPS = 4

/**
 * Compute lesson state from chapter + responses.
 * Pure function.
 */
export function getLessonState(chapter: Chapter, responses: ResponseRecord[]): LessonState {
  const currentStepIndex = Math.min(responses.length, TOTAL_STEPS - 1)
  const isComplete = responses.length >= TOTAL_STEPS

  return {
    chapter,
    currentStepIndex,
    currentStep: chapter.steps[currentStepIndex],
    responses,
    isComplete,
  }
}

/**
 * Check if the current step has been responded to.
 */
export function canAdvance(currentStepIndex: number, responses: ResponseRecord[]): boolean {
  return responses.some((r) => r.step === currentStepIndex)
}

/**
 * Get the next step index (capped at max).
 */
export function advanceStep(currentStepIndex: number): number {
  return Math.min(currentStepIndex + 1, TOTAL_STEPS - 1)
}

/**
 * Load lesson responses from Supabase.
 */
export async function loadLessonResponses(
  childId: string,
  lessonId: string
): Promise<ResponseRecord[]> {
  if (!isSupabaseConfigured()) return []

  const { data, error } = await supabase
    .from('lesson_responses')
    .select('step, choice_made')
    .eq('child_id', childId)
    .eq('lesson_id', lessonId)
    .order('step', { ascending: true })

  if (error) {
    console.error('Failed to load lesson responses:', error)
    return []
  }

  return (data ?? []).map((row) => ({
    step: row.step,
    choiceMade: row.choice_made,
  }))
}

/**
 * Record a response for a lesson step.
 */
export async function recordResponse(
  childId: string,
  lessonId: string,
  step: number,
  choiceMade: string,
  responseData: Record<string, unknown> = {}
): Promise<boolean> {
  if (!isSupabaseConfigured()) return false

  const { error } = await supabase.from('lesson_responses').insert({
    child_id: childId,
    lesson_id: lessonId,
    step,
    choice_made: choiceMade,
    response_data: responseData as Json,
  })

  if (error) {
    console.error('Failed to record lesson response:', error)
    return false
  }

  return true
}
